import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuDownload, LuExternalLink, LuFile, LuFileText, LuPresentation, LuTag, LuVideo } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useSessionHold from '../../../hooks/useSessionHold';
import { useTranslation } from '../../../i18n/LanguageContext';
import ReadConfirmation from './ReadConfirmation';
import { sopKind } from './useDocumentLibrary';

const KIND_ICONS = { pdf: LuFileText, video: LuVideo, ppt: LuPresentation, other: LuFile };

// Okumaya fırsat tanımak için belge yüklendikten bu kadar sonra onay açılır
const READ_DELAY_MS = 3000;

/**
 * Reads an SOP (PDF, PowerPoint through Google's viewer, video) and takes the
 * "I have read it" confirmation. The confirmation unlocks once the document
 * has loaded for a moment, the video has ended or the file was opened; giving
 * it starts the department's short quiz, and passing that marks the SOP read.
 * Confirming again takes the "read" mark back.
 */
const SopViewerModal = ({ sop, isRead, canConfirm, onConfirmReady, onConfirm, onClose, quizOpen }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.viewer.${key}`, vars);
  const kind = sop ? sopKind(sop.fileType) : 'other';
  const Icon = KIND_ICONS[kind];
  const unlocked = canConfirm || isRead;
  // Belge iframe'de ya da yeni sekmede okunurken bu sayfaya olay gelmez;
  // pencere açık kaldıkça hareketsizlik çıkışını uzat
  useSessionHold(Boolean(sop));

  const unlockLater = () => window.setTimeout(onConfirmReady, READ_DELAY_MS);

  return (
    <Modal
      isOpen={Boolean(sop)}
      // Sınav açıkken (kendi penceresinde) Escape bu pencereyi kapatmasın
      onClose={() => !quizOpen && onClose()}
      size="xl"
      title={sop?.name || ''}
      subtitle={label('title')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} className="max-sm:tw-hidden">{t('common.close')}</Button>
          {sop?.fileUrl && (
            <Button
              onClick={() => {
                window.open(sop.fileUrl, '_blank', 'noopener,noreferrer');
                onConfirmReady();
              }}
              className="max-sm:tw-flex-1"
            >
              <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('openInTab')}
            </Button>
          )}
        </>
      )}
    >
      {sop && (
        <div className="tw-space-y-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {sop.versionNumber && (
              <span className="tw-inline-flex tw-items-center tw-gap-1"><LuTag aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('version', { version: sop.versionNumber })}</span>
            )}
            <span className="tw-inline">{t(`profilePage.files.kinds.${kind}`)}</span>
            {isRead && <Badge tone="success"><LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" />{t('profilePage.files.badges.read')}</Badge>}
          </div>

          {kind === 'pdf' && (
            <iframe
              src={`${sop.fileUrl}#toolbar=0&navpanes=0`}
              title={sop.name}
              onLoad={unlockLater}
              className="tw-block tw-h-[min(68vh,720px)] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white dark:tw-border-white/10"
            />
          )}
          {kind === 'ppt' && (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(sop.fileUrl)}&embedded=true`}
              title={sop.name}
              onLoad={unlockLater}
              className="tw-block tw-h-[min(68vh,720px)] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white dark:tw-border-white/10"
            />
          )}
          {kind === 'video' && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video controls onEnded={onConfirmReady} className="tw-block tw-w-full tw-rounded-2xl tw-bg-black">
              <source src={sop.fileUrl} type="video/mp4" />
              {label('noVideo')}
            </video>
          )}
          {kind === 'other' && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-px-6 tw-py-10 tw-text-center dark:tw-border-white/15">
              <LuFile aria-hidden="true" className="tw-h-10 tw-w-10 tw-text-slate-300 dark:tw-text-slate-600" />
              <p className="tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{label('cannotPreview')}</p>
              <a
                href={sop.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onConfirmReady}
                className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-teal-600 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-teal-700"
              >
                <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('download')}
              </a>
            </div>
          )}

          <ReadConfirmation
            isRead={isRead}
            unlocked={unlocked}
            onToggle={onConfirm}
            confirmLabel={label('confirm')}
            lockedHint={label('lockedHint')}
            pendingHint={label('quizHint')}
            readHint={label('readHint')}
          />
        </div>
      )}
    </Modal>
  );
};

SopViewerModal.propTypes = {
  sop: PropTypes.object,
  isRead: PropTypes.bool,
  canConfirm: PropTypes.bool,
  onConfirmReady: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  quizOpen: PropTypes.bool,
};

export default SopViewerModal;
